import { inject, Injectable } from '@angular/core';
import { combineLatest, Observable, ReplaySubject, Subscriber } from 'rxjs';
import { EmailSettings, Role, TestEmailSettings } from '../models/generated/ziroModels';
import { HttpClient } from '@angular/common/http';
import { AuthenticationContext } from './authentication.context';
import { take } from 'rxjs/operators';
import { GlobalPropertiesContext } from './global-properties.context';

@Injectable({
  providedIn: 'root',
})
export class EmailSettingsContext {
  private http = inject(HttpClient);
  private authenticationContext = inject(AuthenticationContext);
  private _globalPropertiesContext = inject(GlobalPropertiesContext);

  _stateSource = new ReplaySubject<EmailSettings>(1);
  state$ = this._stateSource.asObservable();

  constructor() {
    combineLatest([this._globalPropertiesContext.state$, this.authenticationContext.state$])
      .pipe(take(1))
      .subscribe(([globalProperties, currentUser]) => {
        const isAdmin = this.authenticationContext.userIsAtLeast(currentUser, Role.S8_ADMIN);
        if (!globalProperties.hostedEnabled && isAdmin) {
          this._init();
        }
      });
  }

  put(settings: EmailSettings): Observable<void> {
    return new Observable<void>((subscriber: Subscriber<void>) => {
      this.http.put<void>('/services/cisco/admin/e-mail/settings', settings).subscribe(() => {
        this._stateSource.next(settings);
        subscriber.next();
        subscriber.complete();
      });
    });
  }

  test(settings: TestEmailSettings): Observable<any> {
    return this.http.post<any>('/services/cisco/admin/e-mail/settings/test', settings);
  }

  delete(): Observable<void> {
    return this.http.delete<void>('/services/cisco/admin/e-mail/settings');
  }

  private _init(): void {
    this._get().subscribe((data: EmailSettings) => {
      this._stateSource.next(data);
    });
  }

  private _get(): Observable<EmailSettings> {
    return this.http.get<EmailSettings>('/services/cisco/admin/e-mail/settings');
  }
}
